import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { copyToClipboard as copyText } from "@/lib/utils";
import { Copy, Eye, EyeOff, Key, Zap } from "lucide-react";
import { useCallback, useState } from "react";

export default function APIKeysTable({ apiKeys }: { apiKeys: any[] }) {
    const [showApiKey, setShowApiKey] = useState<{ [key: string]: boolean }>({});
    const { toast } = useToast();

    const copyToClipboard = useCallback(
        async (text: string) => {
            try {
                await copyText(text);
                toast({
                    title: "Copied",
                    description: "API key copied to clipboard.",
                    variant: "default",
                });
            } catch (error) {
                toast({
                    title: "Copy failed",
                    description: "Please copy it manually.",
                    variant: "destructive",
                });
            }
        },
        [toast]
    );

    const toggleApiKeyVisibility = useCallback((keyId: string) => {
        setShowApiKey((prev) => ({
            ...prev,
            [keyId]: !prev[keyId],
        }));
    }, []);

    return (
        <div className="space-y-3">
            {/* Table Header - Hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-4 rounded-2xl bg-stone-100/70 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                <div className="col-span-3">Name</div>
                <div className="col-span-5">Key</div>
                <div className="col-span-2">Credits</div>
                <div className="col-span-2">Options</div>
            </div>

            {/* API Keys List */}
            {apiKeys.map((key) => (
                <div key={key.id}>
                    {/* Desktop Layout */}
                    <div className="hidden md:grid grid-cols-12 gap-4 rounded-2xl border border-stone-200/80 bg-[#fffdfa] p-4 items-center transition hover:border-stone-300 hover:shadow-sm">
                        {/* NAME */}
                        <div className="col-span-3">
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-stone-100">
                                    <Key className="h-4 w-4 text-stone-500" />
                                </div>
                                <div className="font-medium text-stone-950 truncate">{key.name}</div>
                            </div>
                        </div>

                        {/* KEY */}
                        <div className="col-span-5">
                            <div className="flex items-center space-x-2">
                                <code className="flex-1 truncate rounded-xl bg-stone-100 px-3 py-2 font-mono text-sm text-stone-700">
                                    {showApiKey[key.id]
                                        ? (key.unkey || 'No key available')
                                        : key.unkey ? `${key.unkey?.substring(0, 8)}-***************************` : 'No key available'
                                    }
                                </code>
                            </div>
                        </div>

                        <div className="col-span-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-stone-950">
                                <Zap className="h-4 w-4 text-emerald-500" />
                                {key.remaining === null || key.remaining === undefined ? 'Unlimited' : key.remaining}
                            </div>
                            {key.refill?.amount && (
                                <div className="text-xs text-stone-500">
                                    {key.refill.amount}/{key.refill.interval}
                                </div>
                            )}
                        </div>

                        {/* OPTIONS */}
                        <div className="col-span-2">
                            <div className="flex items-center space-x-1">
                                {/* Show/Hide Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 rounded-full p-0"
                                    onClick={() =>
                                        setShowApiKey((prev) => ({
                                            ...prev,
                                            [key.id]: !prev[key.id],
                                        }))
                                    }
                                >
                                    {showApiKey[key.id] ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </Button>

                                {/* Copy Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 rounded-full p-0"
                                    onClick={() => copyToClipboard(key.unkey)}
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden space-y-3 rounded-2xl border border-stone-200/80 bg-[#fffdfa] p-4">
                        {/* Header with name and actions */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                                <Key className="w-4 h-4 text-stone-500 flex-shrink-0" />
                                <h4 className="font-medium text-stone-950 truncate">{key.name}</h4>
                            </div>
                            <div className="flex items-center space-x-1 flex-shrink-0">
                                {/* Show/Hide Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 rounded-full p-0"
                                    onClick={() => toggleApiKeyVisibility(key.id)}
                                >
                                    {showApiKey[key.id] ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </Button>

                                {/* Copy Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 rounded-full p-0"
                                    onClick={() => copyToClipboard(key.unkey)}
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* API Key */}
                        <div className="space-y-1">
                            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">API Key</label>
                            <code className="block break-all rounded-xl bg-stone-100 px-3 py-2 font-mono text-sm text-stone-700">
                                {showApiKey[key.id]
                                    ? (key.unkey || 'No key available')
                                    : key.unkey ? `${key.unkey?.substring(0, 8)}${'*'.repeat(Math.max(0, key.unkey.length - 8))}` : 'No key available'
                                }
                            </code>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">Credits</label>
                            <div className="text-sm text-stone-950">
                                {key.remaining === null || key.remaining === undefined ? 'Unlimited' : key.remaining}
                                {key.refill?.amount && (
                                    <span className="ml-2 text-xs text-stone-500">
                                        refills {key.refill.amount}/{key.refill.interval}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
